import { lazy, type ComponentType } from 'react';
import { AlertCircle, Calendar, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useHeader } from '../contexts/HeaderContext';
import { useAnnouncement } from '../hooks/useAnnouncement';
import { AdminNavbarChrome } from './AdminNavbarChrome';
import { ACTIVE_THEME_ID } from '../config/theme/theme';
import { WarmBroadsheetNavbar } from '../pages/themes/warm-broadsheet/Navbar';

// Only the active theme's navbar ships eagerly (this file is part of the
// always-loaded entry bundle); the other 4 themes are dynamic imports so
// their code is never fetched/parsed unless VITE_THEME selects them (fixes
// unused-JS-shipped-on-every-load from the performance audit).
const THEME_NAVBARS: Record<string, ComponentType<any>> = {
  'clinical-modern': lazy(() => import('../pages/themes/clinical-modern/Navbar').then(m => ({ default: m.ClinicalModernNavbar }))),
  'classic-academic': lazy(() => import('../pages/themes/classic-academic/Navbar').then(m => ({ default: m.ClassicAcademicNavbar }))),
  'minimal-scandinavian': lazy(() => import('../pages/themes/minimal-scandinavian/Navbar').then(m => ({ default: m.MinimalScandinavianNavbar }))),
  'field-notes': lazy(() => import('../pages/themes/field-notes/Navbar').then(m => ({ default: m.FieldNotesNavbar }))),
};
const ThemeNavbar = THEME_NAVBARS[ACTIVE_THEME_ID] || WarmBroadsheetNavbar;

// Temporary site-wide outage notice -- flip back to false once the underlying
// hosting/database issue is resolved. Intentionally not dismissible while true.
const SHOW_OUTAGE_BANNER = false;

/**
 * Thin orchestrator: renders the theme-invariant outage/announcement bars, then dispatches
 * to AdminNavbarChrome or the active theme's own Navbar. App.tsx's existing usage
 * (`<Navbar />`) needs no changes -- all theme dispatch happens here.
 */
const Navbar = () => {
  const { setShowAnnouncement, dismissedMessage, isAdmin, isAnnouncementVisible } = useHeader();
  const announcement = useAnnouncement();
  const showAnnouncementBanner = isAnnouncementVisible && announcement.isActive && announcement.message !== dismissedMessage;
  const topBarVisible = SHOW_OUTAGE_BANNER || showAnnouncementBanner;

  return (
    <>
      {SHOW_OUTAGE_BANNER && (
        <div className="fixed w-full z-50 top-0 bg-amber-600 text-white py-2 shadow-md">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center">
              <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
              <span className="text-sm font-medium">
                We're currently experiencing technical difficulties due to an infrastructure outage affecting our hosting provider. Some parts of the site may be temporarily unavailable. We apologize for the disruption and are working to restore full service as soon as possible.
              </span>
            </div>
          </div>
        </div>
      )}

      {showAnnouncementBanner && !SHOW_OUTAGE_BANNER && (
        <div className="fixed w-full z-50 top-0 bg-brick text-white py-2 shadow-md">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center flex-1 min-w-0">
                <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                <span className="text-sm font-medium truncate">
                  {announcement.message}
                </span>
              </div>
              <div className="flex items-center ml-2 flex-shrink-0">
                {announcement.link && (
                  <Link
                    to={announcement.link}
                    className="text-xs whitespace-nowrap font-semibold bg-white text-brick hover:bg-brick-light px-3 py-1 rounded-full flex items-center transition-colors duration-200"
                  >
                    <Calendar className="h-3 w-3 mr-1" />
                    {announcement.linkText || 'Read Now'}
                  </Link>
                )}
                <button
                  onClick={() => setShowAnnouncement(false, announcement.message)}
                  className="text-white hover:text-gray-200 transition-colors p-1 ml-1"
                  aria-label="Close announcement"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isAdmin ? (
        <AdminNavbarChrome topBarVisible={topBarVisible} />
      ) : (
        <ThemeNavbar topBarVisible={topBarVisible} />
      )}
    </>
  );
};

export default Navbar;
